import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AccountDetail, Card, Item, ItemCardBinding } from '../types';
import {
  deleteItemCardBinding,
  getAccountDetails,
  getCards,
  getItemCardBindings,
  getItems,
  updateItemCardBinding,
  upsertItemCardBinding,
} from '../services/api';
import { showToast } from '../utils/toast';
import Select from './Select';
import {
  Box,
  Edit2,
  Link2,
  Loader2,
  Plus,
  Power,
  PowerOff,
  RefreshCw,
  Save,
  Trash2,
  Truck,
  X,
} from 'lucide-react';

type BindingForm = {
  item_id: string;
  card_id: string;
  description: string;
  enabled: boolean;
};

const emptyForm: BindingForm = {
  item_id: '',
  card_id: '',
  description: '',
  enabled: true,
};

const AutoDelivery: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [bindings, setBindings] = useState<ItemCardBinding[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBinding, setEditingBinding] = useState<ItemCardBinding | null>(null);
  const [form, setForm] = useState<BindingForm>(emptyForm);

  const accountOptions = accounts.map(account => ({
    value: account.id,
    label: account.nickname || account.name || account.remark || account.username || account.id,
  }));

  const filteredItems = useMemo(
    () => items.filter(item => !selectedAccount || item.cookie_id === selectedAccount),
    [items, selectedAccount]
  );

  const selectedItem = filteredItems.find(item => item.item_id === form.item_id);

  const getCardStockCount = (card: Card) => {
    if (card.stock_count !== undefined && card.stock_count !== null) return card.stock_count;
    if (card.type !== 'data' || !card.data_content) return null;
    return card.data_content.split('\n').filter(line => line.trim()).length;
  };

  const getCardTypeLabel = (type?: Card['type']) => {
    const labels = {
      api: 'API',
      text: '文本',
      data: '批量',
      image: '图片',
    };
    return type ? labels[type] : '卡密';
  };

  const availableCards = useMemo(() => {
    if (!selectedItem) return cards;
    const itemIsMultiSpec = Boolean(selectedItem.is_multi_spec);
    return cards.filter(card => Boolean(card.is_multi_spec) === itemIsMultiSpec);
  }, [cards, selectedItem]);

  const itemOptions = filteredItems.map(item => ({
    value: item.item_id,
    label: item.item_title || `商品 ${item.item_id}`,
    description: [
      `ID: ${item.item_id}`,
      Boolean(item.is_multi_spec) ? '多规格商品' : '普通商品',
    ].join(' · '),
  }));

  const cardOptions = availableCards.map(card => {
    const stockCount = getCardStockCount(card);
    const specText = card.is_multi_spec && card.spec_name && card.spec_value
      ? `${card.spec_name}:${card.spec_value}`
      : '';
    return {
      value: String(card.id),
      label: card.name || `卡密 ${card.id}`,
      description: [
        getCardTypeLabel(card.type),
        specText,
        stockCount !== null ? `库存 ${stockCount}` : '',
        card.enabled ? '' : '已停用',
      ].filter(Boolean).join(' · '),
      disabled: !card.enabled,
    };
  });

  const loadData = async (accountId = selectedAccount) => {
    setLoading(true);
    try {
      const [latestItems, latestCards, latestBindings] = await Promise.all([
        getItems(),
        getCards(),
        getItemCardBindings(accountId || undefined),
      ]);
      setItems(latestItems);
      setCards(latestCards);
      setBindings(latestBindings);
    } catch (error: any) {
      console.error('加载自动发货数据失败:', error);
      showToast({ type: 'error', message: error?.message || '加载自动发货数据失败' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAccountDetails().then(data => {
      setAccounts(data);
      if (data.length > 0) {
        setSelectedAccount(current => current || data[0].id);
      }
    });
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      loadData(selectedAccount);
    }
  }, [selectedAccount]);

  const openAddModal = () => {
    if (!selectedAccount) {
      showToast({ type: 'warning', message: '请先选择账号' });
      return;
    }
    setEditingBinding(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (binding: ItemCardBinding) => {
    setEditingBinding(binding);
    setForm({
      item_id: binding.item_id,
      card_id: String(binding.card_id),
      description: binding.description || '',
      enabled: binding.enabled,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBinding(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!selectedAccount) {
      showToast({ type: 'warning', message: '请先选择账号' });
      return;
    }
    if (!form.item_id) {
      showToast({ type: 'warning', message: '请选择商品' });
      return;
    }
    if (!form.card_id) {
      showToast({ type: 'warning', message: '请选择卡密' });
      return;
    }

    setSaving(true);
    try {
      if (editingBinding) {
        await updateItemCardBinding(editingBinding.id, {
          card_id: Number(form.card_id),
          enabled: form.enabled,
          description: form.description.trim(),
        });
      } else {
        await upsertItemCardBinding({
          cookie_id: selectedAccount,
          item_id: form.item_id,
          card_id: Number(form.card_id),
          enabled: form.enabled,
          description: form.description.trim(),
        });
      }
      await loadData(selectedAccount);
      closeModal();
      showToast({ type: 'success', message: '自动发货绑定已保存' });
    } catch (error: any) {
      console.error('保存自动发货绑定失败:', error);
      showToast({ type: 'error', message: error?.message || '保存失败，请重试' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (binding: ItemCardBinding) => {
    try {
      await updateItemCardBinding(binding.id, { enabled: !binding.enabled });
      await loadData(selectedAccount);
    } catch (error: any) {
      showToast({ type: 'error', message: error?.message || '状态更新失败' });
    }
  };

  const handleDelete = async (binding: ItemCardBinding) => {
    if (!confirm(`确认删除“${binding.item_title || binding.item_id}”的自动发货绑定吗？`)) return;
    try {
      await deleteItemCardBinding(binding.id);
      await loadData(selectedAccount);
      showToast({ type: 'success', message: '自动发货绑定已删除' });
    } catch (error: any) {
      showToast({ type: 'error', message: error?.message || '删除失败，请重试' });
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">自动发货</h2>
          <p className="mt-2 text-sm text-gray-500">按商品明确绑定卡密，用户付款后自动发送给买家。</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-3 xl:w-auto xl:justify-end">
          <Select
            className="w-full flex-none sm:w-64 xl:w-60 2xl:w-72"
            value={selectedAccount}
            onChange={setSelectedAccount}
            options={[{ value: '', label: '请选择账号' }, ...accountOptions]}
            placeholder="请选择账号"
          />
          <button
            type="button"
            onClick={() => loadData(selectedAccount)}
            disabled={loading || !selectedAccount}
            className="flex h-12 min-w-[112px] flex-1 items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-60 sm:flex-none"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
          <button
            type="button"
            onClick={openAddModal}
            disabled={!selectedAccount}
            className="ios-btn-primary flex h-12 min-w-[132px] flex-1 items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold shadow-lg shadow-yellow-200 disabled:opacity-60 sm:flex-none"
          >
            <Plus className="h-4 w-4" />
            添加绑定
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-gray-400">
          <Loader2 className="h-10 w-10 animate-spin text-[#FFE815]" />
        </div>
      ) : bindings.length > 0 ? (
        <div className="space-y-4">
          {bindings.map(binding => (
            <div
              key={binding.id}
              className="ios-card rounded-3xl p-5 transition-all hover:shadow-lg"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                  <div className={`flex h-14 w-14 flex-none items-center justify-center rounded-2xl ${
                    binding.enabled ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
                  }`}>
                    <Truck className="h-7 w-7" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <h3 className="max-w-xl truncate text-lg font-extrabold text-gray-900">
                        {binding.item_title || binding.item_id}
                      </h3>
                      <span className={`rounded-lg px-2.5 py-1 text-xs font-bold ${
                        binding.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {binding.enabled ? '已启用' : '已停用'}
                      </span>
                      {binding.spec_name && binding.spec_value && (
                        <span className="rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">
                          {binding.spec_name}:{binding.spec_value}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium text-gray-500">
                      <span className="rounded-lg bg-gray-100 px-2 py-1">商品ID: {binding.item_id}</span>
                      <span className="rounded-lg bg-gray-100 px-2 py-1">卡密: {binding.card_name || binding.card_id}</span>
                      <span className="rounded-lg bg-gray-100 px-2 py-1">{getCardTypeLabel(binding.card_type)}</span>
                      {binding.stock_count !== null && binding.stock_count !== undefined && (
                        <span className="rounded-lg bg-gray-100 px-2 py-1">库存 {binding.stock_count}</span>
                      )}
                      <span className="rounded-lg bg-gray-100 px-2 py-1">已发 {binding.delivery_times || 0} 次</span>
                    </div>
                    {binding.description && (
                      <p className="mt-2 line-clamp-1 text-sm text-gray-500">{binding.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-none items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleToggle(binding)}
                    className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${
                      binding.enabled
                        ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                    title={binding.enabled ? '停用' : '启用'}
                  >
                    {binding.enabled ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditModal(binding)}
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-600 transition-colors hover:bg-[#FFE815] hover:text-gray-900"
                    title="编辑"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(binding)}
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-500 transition-colors hover:bg-red-100"
                    title="删除"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="ios-card rounded-3xl py-24 text-center text-gray-400">
          <Box className="mx-auto mb-4 h-12 w-12 opacity-40" />
          <h3 className="mb-2 text-xl font-bold text-gray-700">暂无自动发货绑定</h3>
          <p className="text-sm font-medium text-gray-500">添加商品与卡密的绑定后，付款订单才会触发自动发货。</p>
        </div>
      )}

      {showModal && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container">
            <div className="modal-header">
              <div className="flex w-full items-center justify-between">
                <div>
                  <h3 className="text-2xl font-extrabold text-gray-900">
                    {editingBinding ? '编辑自动发货' : '添加自动发货'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">选择商品和卡密，建立付款后的自动发送关系。</p>
                </div>
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-full bg-gray-100 p-2 transition-colors hover:bg-gray-200"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="modal-body space-y-5">
              <div>
                <label className="mb-2 block text-sm font-bold text-gray-700">关联商品</label>
                <Select
                  value={form.item_id}
                  onChange={(value) => setForm({ ...form, item_id: value, card_id: '' })}
                  className="w-full"
                  options={[{ value: '', label: '请选择商品' }, ...itemOptions]}
                  placeholder="请选择商品"
                  emptyLabel="当前账号暂无商品，请先同步商品列表"
                  disabled={Boolean(editingBinding)}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-gray-700">
                  {selectedItem && Boolean(selectedItem.is_multi_spec) ? '关联多规格卡密' : '关联卡密'}
                </label>
                <Select
                  value={form.card_id}
                  onChange={(value) => setForm({ ...form, card_id: value })}
                  className="w-full"
                  options={[{ value: '', label: '请选择卡密' }, ...cardOptions]}
                  placeholder="请选择卡密"
                  emptyLabel={selectedItem && Boolean(selectedItem.is_multi_spec) ? '暂无可用多规格卡密' : '暂无可用普通卡密'}
                  disabled={!form.item_id}
                />
                {selectedItem && (
                  <p className="mt-2 text-xs font-medium text-gray-500">
                    {Boolean(selectedItem.is_multi_spec)
                      ? '多规格商品只展示多规格卡密，发货时按订单规格精确匹配。'
                      : '普通商品只展示普通卡密。'}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-gray-700">备注（可选）</label>
                <input
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  className="w-full ios-input rounded-xl px-4 py-3"
                  placeholder="例如：主卡密池、备用规格、活动批次"
                />
              </div>

              <div className="flex items-center justify-between rounded-xl bg-gray-50 p-4">
                <div className="flex items-center gap-3">
                  <Link2 className="h-5 w-5 text-gray-600" />
                  <span className="text-sm font-bold text-gray-900">启用自动发货</span>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, enabled: !form.enabled })}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                    form.enabled ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform ${
                      form.enabled ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex w-full gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 rounded-xl bg-gray-100 px-6 py-3 font-bold text-gray-700 transition-colors hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !form.item_id || !form.card_id}
                  className="ios-btn-primary flex flex-1 items-center justify-center gap-2 rounded-xl px-6 py-3 font-bold disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AutoDelivery;
